import React, { useCallback, useEffect, useRef, useState } from 'react';
import Layout from 'components/sales/layout/layout';
import localization from 'assets/localization/localization';
import TextAreaInput from 'components/sales/Input/textAreaInput/textAreaInput';
import useLatLng from 'hooks/uselatlng/uselatlng';
import GoogleMapReact from 'google-map-react';
import { toast } from 'react-toastify';
import { reverseGeocode } from 'services/utils/location/reverseGeocode';
import './darwinCheckInOut.css';

const ATTENDANCE_STEP = {
  LOADING: 'loading',
  READY: 'ready',
};

const formatDarwinDateTime = () => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');

  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
};

const DarwinCheckInOut = ({ 
  checkUserLocation, 
  userLocation,
  checkDarwinAttendance,
  submitDarwinAttendance,
  darwinAttendance,
  getProfile,
}) => {
  const islatlng = useLatLng();
  const mapRef = useRef(null);
  const mapsRef = useRef(null);
  const markerRef = useRef(null);
  const infoWindowRef = useRef(null);
  const [attendanceStep, setAttendanceStep] = useState(ATTENDANCE_STEP.LOADING);
  const [checkType, setCheckType] = useState('in');
  const [addressDetails, setAddressDetails] = useState(null);
  const [isAddressLoading, setIsAddressLoading] = useState(false);
  const [comments, setComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFiled, setIsFiled] = useState(false);

  const isOutOfOffice =
    userLocation?.location_type?.toLowerCase() === 'out of office';
  const isInOffice =
    userLocation?.location_type?.toLowerCase() === 'in-office' ||
    userLocation?.location_type?.toLowerCase() === 'in office';

  const pageTitle =
    checkType === 'out'
      ? localization.darwinCheckOut
      : localization.darwinCheckIn;

  const updateLocationMarker = useCallback((map, maps) => {
    const activeMap = map || mapRef.current;
    const activeMaps = maps || mapsRef.current;

    if (!activeMap || !activeMaps || !islatlng || islatlng === false) {
      return;
    }

    const position = {
      lat: Number(islatlng.lat),
      lng: Number(islatlng.lng),
    };

    if (markerRef.current && markerRef.current.getMap() === activeMap) {
      markerRef.current.setPosition(position);
    } else {
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }

      markerRef.current = new activeMaps.Marker({
        position,
        map: activeMap,
        title: 'You are here!',
        icon: {
          url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
          scaledSize: new activeMaps.Size(48, 48),
        },
      });
    }

    activeMap.panTo(position);

    const locationLabel =
      addressDetails?.formattedAddress ||
      `${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`;

    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }

    infoWindowRef.current = new activeMaps.InfoWindow({
      content: `<div class="darwin-attendance-map-info">${locationLabel}</div>`,
    });
    infoWindowRef.current.open(activeMap, markerRef.current);
  }, [islatlng, addressDetails]);

  const handleGoogleApiLoaded = ({ map, maps }) => {
    mapRef.current = map;
    mapsRef.current = maps;
    markerRef.current = null;
    infoWindowRef.current = null;
    updateLocationMarker(map, maps);
  };

  useEffect(() => {
    if (mapRef.current && mapsRef.current) {
      updateLocationMarker(mapRef.current, mapsRef.current);
    }
  }, [updateLocationMarker]);

  useEffect(() => {
    const loadAttendance = async () => {
      const result = await checkDarwinAttendance();

      if (!result || result?.status !== 0) {
        toast.error(result?.message || 'Unable to fetch attendance details');
        setAttendanceStep(ATTENDANCE_STEP.READY);
        return;
      }

      setCheckType(result?.data ? 'out' : 'in');
      setAttendanceStep(ATTENDANCE_STEP.READY);
    };

    loadAttendance();
  }, [checkDarwinAttendance]);

  useEffect(() => {
    if (darwinAttendance) {
      setCheckType('out');
    }
  }, [darwinAttendance]);

  useEffect(() => {
    const loadAddress = async () => {
      if (!islatlng || islatlng === false) {
        setAddressDetails(null);
        return;
      }

      setIsAddressLoading(true);

      const result = await reverseGeocode(islatlng.lat, islatlng.lng);
      if (result) {
        setAddressDetails(result);
      }

      setIsAddressLoading(false);
    };

    loadAddress();
  }, [islatlng]);

  useEffect(() => {
    const loadLocationType = async () => {
      if (!islatlng || islatlng === false) {
        return;
      }

      const result = await checkUserLocation(islatlng.lat, islatlng.lng);

      if (result?.status !== 0) {
        toast.error(result?.data?.message || result?.message || 'Something went wrong');
      }
    };

    loadLocationType();
  }, [checkUserLocation, islatlng]);

  const getLocationLabel = () => {
    if (!islatlng || islatlng === false) {
      return localization.locationPermission;
    }

    const lat = Number(islatlng.lat).toPrecision(8);
    const lng = Number(islatlng.lng).toPrecision(8);
    const locality = addressDetails?.locality || addressDetails?.formattedAddress;

    return locality ? `${lat}, ${lng} | ${locality}` : `${lat}, ${lng}`;
  };

  const getLocationTypeLabel = () => {
    if (!userLocation?.location_type) {
      return '';
    }

    if (isOutOfOffice) {
      if (isAddressLoading) {
        return `${userLocation.location_type} | Fetching location...`;
      }

      if (addressDetails?.formattedAddress) {
        return `${userLocation.location_type} | ${addressDetails.formattedAddress}`;
      }

      return userLocation.location_type;
    }

    if (userLocation.office_name) {
      return `${userLocation.location_type} | ${userLocation.office_name}`;
    }

    return userLocation.location_type;
  };

  const handleSubmit = async () => {
    if (!comments?.trim()) {
      setIsFiled(true);
      return;
    }

    if (!islatlng || islatlng === false) {
      toast.error(localization.locationPermission);
      return;
    }

    if (!userLocation?.location_type) {
      toast.error('Unable to determine location type');
      return;
    }

    setIsFiled(false);
    setIsSubmitting(true);

    try {
      let payload;
      const remarks = comments.trim();
      const currentTime = formatDarwinDateTime();
      const latitude = Number(islatlng.lat);
      const longitude = Number(islatlng.lng);

      if (checkType === 'in') {
        payload = {
          action: 'check_in',
          type: userLocation.location_type,
          check_in_latitude: latitude,
          check_in_longitude: longitude,
          check_in_time: currentTime,
          remarks,
        };

        if (isInOffice) {
          if (!userLocation.office_id) {
            toast.error('Office details not found');
            return;
          }

          payload.office_id = userLocation.office_id;
        }

        if (isOutOfOffice) {
          if (!addressDetails?.formattedAddress) {
            toast.error('Unable to fetch current address');
            return;
          }

          payload.current_address = addressDetails.formattedAddress;
        }
      } else {
        const meetingId =
          darwinAttendance?.meeting_id || darwinAttendance?.id;

        if (!meetingId) {
          toast.error('Meeting details not found');
          return;
        }

        payload = {
          action: 'check_out',
          meeting_id: meetingId,
          check_out_latitude: latitude,
          check_out_longitude: longitude,
          check_out_time: currentTime,
          remarks,
        };

        if (isOutOfOffice) {
          if (!addressDetails?.formattedAddress) {
            toast.error('Unable to fetch current address');
            return;
          }

          payload.current_address = addressDetails.formattedAddress;
        }
      }

      const result = await submitDarwinAttendance(payload);

      if (result?.status === 0) {
        toast.success(result.message || 'Success');
        setComments('');
        setIsFiled(false);
        const attendance = await checkDarwinAttendance();
        if (typeof getProfile === 'function') {
          await getProfile();
        }
        if (attendance?.status === 0 && attendance?.data) {
          setCheckType('out');
        } else {
          setCheckType('in');
        }
      } else {
        toast.error(result?.message || result?.data?.message || 'Something went wrong');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLocationDenied = islatlng === false;
  const isLocationLoading = islatlng === null;
  const showMap = !isLocationDenied && !isLocationLoading && islatlng;

  return (
    <Layout
      className={`darwin-attendance-layout${isFiled ? ' requiredFileds' : ''}`}
      title={pageTitle}
      isLoading={attendanceStep === ATTENDANCE_STEP.LOADING || isLocationLoading}
      pagination={false}
    >
      <div className="darwin-attendance-page">
        <div className="darwin-attendance-card">
          <div className="darwin-attendance-main">
            {showMap && (
              <div className="darwin-attendance-map">
                <GoogleMapReact
                  key={`${islatlng.lat}-${islatlng.lng}`}
                  bootstrapURLKeys={{
                    key: process.env.REACT_APP_GOOGLE_API_KEY,
                  }}
                  defaultCenter={{
                    lat: Number(islatlng.lat),
                    lng: Number(islatlng.lng),
                  }}
                  defaultZoom={16}
                  yesIWantToUseGoogleMapApiInternals
                  onGoogleApiLoaded={handleGoogleApiLoaded}
                  options={{
                    zoomControl: true,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: true,
                  }}
                />
              </div>
            )}

            <div className="darwin-attendance-details">
              <div className="darwin-attendance-location">
                <p className="darwin-attendance-location-label">
                  {localization.currentLocation}
                </p>
                <p className="darwin-attendance-location-value">
                  {getLocationLabel()}
                </p>
              </div>

              {userLocation?.location_type && (
                <div className="darwin-attendance-location">
                  <p className="darwin-attendance-location-label">
                    {localization.locationType}
                  </p>
                  <p className="darwin-attendance-location-value">
                    {getLocationTypeLabel()}
                  </p>
                </div>
              )}

              <div className="darwin-attendance-comments">
                <TextAreaInput
                  type="text"
                  rows={4}
                  className="inline-css scrollbar-styling"
                  label={localization.comments}
                  required
                  isStarRequired
                  errorMsg={localization.isRequired}
                  handleChange={(e) => setComments(e.target.value)}
                  value={comments}
                  inputValue={comments}
                />
              </div>

              <div className="darwin-attendance-actions">
                <button
                  type="button"
                  className="darwin-attendance-submit"
                  onClick={handleSubmit}
                  disabled={isSubmitting || isLocationDenied || isLocationLoading}
                >
                  {isSubmitting ? localization.submitting : pageTitle}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DarwinCheckInOut;
